import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  type ReactNode,
} from "react"
import { useTranslation } from "react-i18next"
import { useLocation } from "react-router"

import { AutosizeTextAreaRef } from "@f2e/ui/components/base-plus"

import { useCopilotSession } from "@/hooks"
import {
  configResponseToCopilotConfig,
  historyResponseToCopilotHistory,
  useGetCopilotConfigApi,
  useGetCopilotHistoriesApi,
  useUploadCopilotFileApi,
} from "@/hooks/api/copilot"
import { getWorkspaceIdFromPath } from "@/lib/utils"
import {
  ConversationType,
  CopilotConfig,
  CopilotHistory,
  FileAttachmentScope,
  FileAttachmentType,
} from "@/types"

import { buildPageSnapshotXml } from "./pageSnapshotBuilder"
import { getCopilotPageKey } from "./pageKey"
import CopilotAlert from "./CopilotAlert"
import CopilotChat from "./CopilotChat"
import CopilotHeader from "./CopilotHeader"
import CopilotInput from "./CopilotInput"
import type {
  CopilotContainerRef,
  CopilotDraftProps,
  CopilotFile,
  CopilotMode,
  CopilotSurfaceRenderProps,
} from "./types"

interface AlertInfo {
  title: string
  description: string
}

export interface CopilotSurfaceProps extends CopilotDraftProps {
  mode: CopilotMode
  isActive: boolean
  newChatRequestNonce?: number | null
  onNewChatRequestHandled?: (nonce: number) => void
  onModeToggle: () => void
  onClose: () => void
  children: (props: CopilotSurfaceRenderProps) => ReactNode
}

const CopilotSurface = forwardRef<CopilotContainerRef, CopilotSurfaceProps>(
  (
    {
      mode,
      isActive,
      newChatRequestNonce = null,
      onNewChatRequestHandled,
      onModeToggle,
      onClose,
      children,
      draft,
      isDraftSendPending,
      onDraftChange,
      onDraftSendBegin,
      onDraftSendAccepted,
      onDraftSendFailed,
      onDraftSendCompleted,
    },
    ref
  ) => {
    const { t, i18n } = useTranslation()
    const location = useLocation()
    const [alertInfo, setAlertInfo] = useState<AlertInfo | null>(null)
    const inputRef = useRef<AutosizeTextAreaRef>(null)
    const initRanRef = useRef(false)
    const isMountedRef = useRef(true)
    const lastHandledNewChatRequestNonceRef = useRef<number | null>(null)
    const sendInFlightRef = useRef(false)

    const [histories, setHistories] = useState<CopilotHistory[]>([])
    const [config, setConfig] = useState<CopilotConfig | undefined>(undefined)
    const [isLoadingSavedConversation, setIsLoadingSavedConversation] =
      useState(false)
    const [isPreparingSend, setIsPreparingSend] = useState(false)

    const workspaceId = getWorkspaceIdFromPath(location.pathname)

    const {
      fetchFn: getCopilotHistoriesApi,
      data: historiesResp,
      error: historiesError,
    } = useGetCopilotHistoriesApi()
    const {
      fetchFn: getCopilotConfigApi,
      data: configResp,
      error: configError,
    } = useGetCopilotConfigApi()
    const { fetchFn: uploadCopilotFile } = useUploadCopilotFileApi()

    useImperativeHandle(ref, () => ({
      focusInput: () => {
        inputRef.current?.textArea.focus({ preventScroll: true })
      },
    }))

    useEffect(() => {
      isMountedRef.current = true
      return () => {
        isMountedRef.current = false
      }
    }, [])

    const refreshHistories = useCallback(() => {
      getCopilotHistoriesApi({
        searchParams: new URLSearchParams({
          limit: "10",
          offset: "0",
        }),
      })
    }, [getCopilotHistoriesApi])

    const {
      chatSession,
      historyId,
      isAsking,
      parts,
      initConversation,
      loadConversationParts,
      getLastHistoryId,
    } = useCopilotSession({
      workspaceId,
      getUserLocale: () => i18n.language,
      onHistoryPrepared: () => {
        refreshHistories()
      },
    })

    const showAlert = useCallback((title: string, description: string) => {
      setAlertInfo({ title, description })
      setTimeout(() => {
        setAlertInfo(null)
      }, 5000)
    }, [])

    const buildCurrentPageSnapshotXml = useCallback((): string | undefined => {
      const pageKey = getCopilotPageKey(
        window.location.pathname,
        window.location.search
      )
      if (pageKey) {
        return buildPageSnapshotXml({
          pageKey,
          filter: (_el, key) => key !== "threadPanel.input",
        })
      }
      return undefined
    }, [])

    const sendNewConversation = useCallback(
      async (
        question: string,
        files: CopilotFile[] | undefined,
        callbacks: {
          onUserPromptAccepted: () => void
          onError: (error: Error) => void
        }
      ): Promise<void> => {
        if (!question.trim()) return

        const currentPageUrl = window.location.href
        let attachedUuids: string[] =
          files?.map((file) => file.uuid!).filter(Boolean) || []
        try {
          const xml = buildCurrentPageSnapshotXml()
          if (xml && typeof xml === "string" && xml.length > 0) {
            const formData = new FormData()
            formData.append("content", xml)
            formData.append("type", FileAttachmentType.PageSnapshot)
            formData.append("scope", FileAttachmentScope.Ephemeral)
            const resp = await uploadCopilotFile({ body: formData })
            const snapshotUuid = resp?.data?.uuid
            if (snapshotUuid) attachedUuids = [...attachedUuids, snapshotUuid]
          }
        } catch (error) {
          console.debug("snapshot upload failed", error)
        }

        await chatSession.sendMessage(question, {
          attachedFileUuids: attachedUuids,
          currentPageUrl,
          ...callbacks,
        })
      },
      [chatSession, buildCurrentPageSnapshotXml, uploadCopilotFile]
    )

    const handleNewChat = useCallback(() => {
      if (isAsking) return
      setIsLoadingSavedConversation(false)
      initConversation({
        question: "",
        type: ConversationType.Conversation,
      })
      refreshHistories()
    }, [initConversation, isAsking, refreshHistories])

    const handleNewChatRequest = useCallback(
      (nonce: number) => {
        lastHandledNewChatRequestNonceRef.current = nonce
        onNewChatRequestHandled?.(nonce)
        handleNewChat()
      },
      [handleNewChat, onNewChatRequestHandled]
    )

    useEffect(() => {
      if (!isActive) return
      if (isAsking) return

      if (initRanRef.current) {
        console.debug("[CopilotSurface] Initialization already ran, skipping")
        return
      }

      console.debug("[CopilotSurface] Running initialization")
      initRanRef.current = true

      if (newChatRequestNonce !== null) {
        handleNewChatRequest(newChatRequestNonce)
        getCopilotConfigApi()
        return
      }

      const lastHistoryId = getLastHistoryId()
      if (lastHistoryId !== undefined) {
        console.debug("[CopilotSurface] Loading conversation:", lastHistoryId)
        setIsLoadingSavedConversation(true)
        loadConversationParts(lastHistoryId)
          .then(() => {
            if (!isMountedRef.current) return
            setIsLoadingSavedConversation(false)
          })
          .catch(() => {
            if (!isMountedRef.current) return
            console.debug(
              "[CopilotSurface] Failed to load conversation, initializing new"
            )
            setIsLoadingSavedConversation(false)
            initConversation({
              question: "",
              type: ConversationType.Conversation,
            })
          })
      } else {
        console.debug(
          "[CopilotSurface] No saved conversation, initializing new"
        )
        initConversation({
          question: "",
          type: ConversationType.Conversation,
        })
      }

      refreshHistories()
      getCopilotConfigApi()
    }, [
      getCopilotConfigApi,
      getLastHistoryId,
      handleNewChatRequest,
      initConversation,
      isActive,
      isAsking,
      loadConversationParts,
      newChatRequestNonce,
      refreshHistories,
    ])

    useEffect(() => {
      if (!isActive) return
      if (isAsking) return
      if (newChatRequestNonce === null) return
      if (!initRanRef.current) return
      if (newChatRequestNonce === lastHandledNewChatRequestNonceRef.current) {
        return
      }

      handleNewChatRequest(newChatRequestNonce)
    }, [handleNewChatRequest, isActive, isAsking, newChatRequestNonce])

    useEffect(() => {
      if (!historiesResp?.data || historiesError) return
      setHistories(
        historiesResp.data.map(historyResponseToCopilotHistory) || []
      )
    }, [historiesError, historiesResp])

    useEffect(() => {
      if (!configResp?.data || configError) return
      setConfig(configResponseToCopilotConfig(configResp.data))
    }, [configError, configResp])

    const handleSend = async (message: string, files?: CopilotFile[]) => {
      if (!message.trim() || isAsking || sendInFlightRef.current) {
        return
      }
      const requestId = onDraftSendBegin(draft)
      if (requestId === null) return

      sendInFlightRef.current = true
      setIsPreparingSend(true)
      let errorHandled = false
      try {
        await sendNewConversation(message, files, {
          onUserPromptAccepted: () => {
            onDraftSendAccepted(requestId)
          },
          onError: (error) => {
            errorHandled = true
            onDraftSendFailed(requestId)
            console.error("error sending message", error)
            showAlert(
              t("common.copilot.errors.sendMessageFailed.title"),
              t("common.copilot.errors.tryAgainLater")
            )
          },
        })
      } catch (error) {
        if (!errorHandled) {
          onDraftSendFailed(requestId)
          console.error("error sending message", error)
          showAlert(
            t("common.copilot.errors.sendMessageFailed.title"),
            t("common.copilot.errors.tryAgainLater")
          )
        }
      } finally {
        onDraftSendCompleted(requestId)
        sendInFlightRef.current = false
        setIsPreparingSend(false)
      }
    }

    const handleRegenerateGroup = useCallback(
      (groupId: string) => {
        if (!groupId || isAsking) {
          return
        }
        void chatSession.regenerate(groupId)
      },
      [chatSession, isAsking]
    )

    const handleSelectHistory = (history: CopilotHistory) => {
      if (isAsking) return
      if (historyId === parseInt(history.id)) return
      void loadConversationParts(parseInt(history.id))
    }

    const header = (
      <CopilotHeader
        className="shrink-0"
        mode={mode}
        histories={histories}
        selectedHistoryId={historyId ? String(historyId) : undefined}
        isBusy={isAsking}
        onModeToggle={onModeToggle}
        onNewChat={handleNewChat}
        onSelectHistory={handleSelectHistory}
        onClose={onClose}
      />
    )

    const content = (
      <>
        {alertInfo ? (
          <CopilotAlert
            title={alertInfo.title}
            description={alertInfo.description}
          />
        ) : null}
        <CopilotChat
          className="flex-1 min-h-0"
          parts={parts}
          config={config}
          onSuggestedPromptClick={onDraftChange}
          onRegenerateGroup={handleRegenerateGroup}
          onCancelHistoryAnalysis={chatSession.cancelHistoryAnalysis}
          isAnswering={isAsking}
          isLoading={isLoadingSavedConversation}
        />
      </>
    )

    const input = (
      <CopilotInput
        ref={inputRef}
        value={draft}
        onValueChange={onDraftChange}
        onSend={handleSend}
        isSending={isAsking || isPreparingSend || isDraftSendPending}
        onCancel={chatSession.cancel}
      />
    )

    return <>{children({ header, content, input })}</>
  }
)

CopilotSurface.displayName = "CopilotSurface"

export default CopilotSurface
